import { useState, useRef, useEffect, useMemo } from 'react';
import { useFetch } from "../../../../../../hooks/useFetch";
import callToast, { baseUrl, payloadDateFormat } from "../../../../../../utils/helper";
import { useStore } from "../../../../../../GlobalProvider/GlobalProvider";
import { Toast } from "primereact/toast";
import { useNavigate } from 'react-router-dom';
import CreateNewAppointment from "../../../../../CreateNewAppointment/CreateNewAppointment";
import ExistingPatient from "../../../../../ExistingPatient/ExistingPatient";
import { useSelector } from 'react-redux';
import { MdKeyboardBackspace } from 'react-icons/md';

const CreateAppointment = () => {
    const { token } = useStore();
    const toast = useRef<any>(null);
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('createNew');
    const [patientSearch, setPatientSearch] = useState('');
    const [, setDoctorList] = useState<any>([]);
    const [searchKey, setSearchKey] = useState<any>("patientId");
    const { selectedRole, selectedDoctor } = useSelector((state: any) => state.auth);

    const { data: locationData } = useFetch(`/location/list`);
    const { data: Patients } = useFetch(
        `/patient/search${patientSearch ? `?doctorId=${selectedDoctor?._id}&search=${patientSearch}` : ""}&key=${searchKey}`,
        [patientSearch]
    );

    const [formData, setFormData] = useState<any>({
        name: '', patientId: '', phoneNumber: '', email: '',
        age: '', ageType: 'years', dateOfBirth: '', bloodGroup: '',
        gender: '', maritalStatus: '', visitLocation: '',
        appointmentDate: payloadDateFormat(new Date()),
        appointmentType: 'new', doctorId: '', _id: ''
    });

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        let payload: any = {
            appointmentDate: formData?.appointmentDate,
            gender: formData?.gender,
            type: formData?.appointmentType,
            name: formData?.name,
            phone: formData?.phoneNumber,
            email: formData?.email,
            dob: formData?.dateOfBirth,
            bloodGroup: formData?.bloodGroup,
            maritalStatus: formData?.maritalStatus,
            doctorId: selectedDoctor?._id,
            officeLocationId: selectedRole?.location?._id,
        };
        if (formData?._id) payload._id = formData?._id;
        if (formData?.patient && formData?.patientId) {
            payload = { ...payload, patientId: formData?.patientId, patient: formData?.patient };
        }
        try {
            const res = await fetch(`${baseUrl}/appointment/create`, {
                method: "POST",
                headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (res.ok) { callToast(toast, true, `Appointment Created successfully`); navigate('/'); }
        } catch (error: any) { console.error(error); }
    };

    const handlePatientSelect = (patient: any) => {
        setFormData({
            ...formData,
            name: patient?.name, patientId: patient?.patientId, patient: patient?._id,
            phoneNumber: patient?.phone, email: patient?.email,
            dateOfBirth: patient?.dob?.substring(0, 10), gender: patient?.gender,
            maritalStatus: patient?.maritalStatus, bloodGroup: patient?.bloodGroup,
            address: patient?.address, _id: patient?._id
        });
        setActiveTab('createNew');
        setPatientSearch('');
    };

    const calculateDobFromAge = (age: any, ageType: any) => {
        const numericAge = parseFloat(age);
        if (isNaN(numericAge)) return;
        const dob = new Date();
        const whole = Math.floor(numericAge);
        const frac = numericAge - whole;
        if (ageType === 'years') {
            dob.setFullYear(dob.getFullYear() - whole);
            if (frac) dob.setMonth(dob.getMonth() - Math.round(frac * 12));
        } else if (ageType === 'months') {
            dob.setMonth(dob.getMonth() - whole);
            if (frac) dob.setDate(dob.getDate() - Math.round(frac * 30));
        } else if (ageType === 'days') {
            dob.setDate(dob.getDate() - Math.round(numericAge));
        }
        setFormData((prev: any) => ({ ...prev, dateOfBirth: dob?.toISOString().split('T')[0] }));
    };

    const calculateAgeFromDob = (dob: any) => {
        const birthDate = new Date(dob);
        const diffMs = new Date().getTime() - birthDate.getTime();
        if (isNaN(diffMs) || diffMs < 0) return;
        const days = diffMs / (1000 * 60 * 60 * 24);
        let age: number;
        let ageType: string;
        if (days >= 365) {
            age = Math.round((days / 365.25) * 10) / 10;
            ageType = 'years';
        } else if (days >= 30) {
            age = Math.round((days / 30.4375) * 10) / 10;
            ageType = 'months';
        } else {
            age = Math.round(days);
            ageType = 'days';
        }
        setFormData((prev: any) => ({ ...prev, age, ageType }));
    };

    useEffect(() => {
        if (formData.age && formData.ageType) calculateDobFromAge(formData.age, formData.ageType);
    }, [formData.age, formData.ageType]);

    useEffect(() => {
        if (formData.dateOfBirth) calculateAgeFromDob(formData.dateOfBirth);
    }, [formData.dateOfBirth]);

    useMemo(() => { if (searchKey) setPatientSearch(""); }, [searchKey]);

    return (
        <div className="px-3 pb-10 max-w-2xl mx-auto">
            <Toast ref={toast} />

            {/* App bar */}
            <div className="flex items-center gap-3 py-3">
                <button
                    className="p-2 rounded-xl bg-white border border-gray-200 shadow-sm active:bg-gray-50"
                    onClick={() => navigate('/')}
                    aria-label="Go back"
                >
                    <MdKeyboardBackspace className="text-gray-600" size={20} />
                </button>
                <p className="font-bold text-gray-800 text-base">New Appointment</p>
            </div>

            {/* Segmented control tabs */}
            <div className="flex bg-gray-100 p-1 rounded-xl mb-4">
                {[
                    { id: 'createNew', label: 'New Patient' },
                    { id: 'existingPatient', label: 'Existing Patient' },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-white shadow-sm text-[#12AB81]' : 'text-gray-500'}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'createNew' && (
                <CreateNewAppointment
                    formData={formData}
                    handleSubmit={handleSubmit}
                    locationData={locationData}
                    doctorList={selectedRole?.parentUsers}
                    setFormData={setFormData}
                    setDoctorList={setDoctorList}
                />
            )}

            {activeTab === 'existingPatient' && (
                <ExistingPatient
                    setSearchKey={setSearchKey}
                    searchKey={searchKey}
                    patientSearch={patientSearch}
                    setPatientSearch={setPatientSearch}
                    Patients={Patients}
                    handlePatientSelect={handlePatientSelect}
                />
            )}
        </div>
    );
};

export default CreateAppointment;
